import { useState, useEffect, useRef, type FC } from "react";
import { HiChevronDown, HiLocationMarker } from "react-icons/hi";

interface LocationIQResult {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    house_number?: string;
    road?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    country?: string;
  };
}

interface LocationIQSearchProps {
  accessToken: string;
  onRetrieve: (result: LocationIQSearchResult) => void;
  value: string;
  placeholder?: string;
  options?: {
    language?: string;
    country?: string;
    proximity?: string;
  };
}

interface LocationIQSearchResult {
  features: Array<{
    place_name: string;
    geometry: {
      coordinates: [number, number]; // [longitude, latitude]
    };
    properties: {
      full_address: string;
      context: {
        address: {
          street_name: string;
          address_number: string;
        };
        place: {
          name: string;
        };
        postcode: {
          name: string;
        };
        country: {
          name: string;
        };
      };
    };
  }>;
}

const LocationIQSearch: FC<LocationIQSearchProps> = ({
  accessToken,
  onRetrieve,
  value,
  placeholder = "Enter an address",
  options = {},
}) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<LocationIQResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastSelectedAddress, setLastSelectedAddress] = useState<string>("");
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cacheRef = useRef<Map<string, { results: LocationIQResult[]; timestamp: number }>>(new Map());

  // Update query when value prop changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Debounced search function
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      // Don't search if the current query is the same as the last selected address
      if (query !== lastSelectedAddress) {
        await performSearch(query);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, options, lastSelectedAddress]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = async (searchQuery: string, attemptCount = 0) => {
    if (!searchQuery || !accessToken) return;

    // Check cache first (5 minute cache)
    const cacheKey = `${searchQuery}_${options.language || 'de'}_DE_${options.proximity || ''}`;
    const cached = cacheRef.current.get(cacheKey);
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes
    
    if (cached && Date.now() - cached.timestamp < cacheExpiry) {
      console.log('Using cached results for:', searchQuery);
      setResults(cached.results);
      setIsOpen(cached.results.length > 0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        key: accessToken,
        q: searchQuery,
        format: "json",
        addressdetails: "1",
        limit: "5",
        accept_language: options.language || "de",
        countrycodes: "DE",
      });

      // Add proximity if provided
      if (options.proximity) {
        const [lon, lat] = options.proximity.split(",");
        if (lon && lat) {
          const longitude = parseFloat(lon);
          const latitude = parseFloat(lat);
          params.append("viewbox", `${longitude - 0.1},${latitude + 0.1},${longitude + 0.1},${latitude - 0.1}`);
          params.append("bounded", "1");
        }
      }

      const response = await fetch(
        `https://eu1.locationiq.com/v1/search.php?${params.toString()}`,
        {
          signal: AbortSignal.timeout(10000), // 10 second timeout
        }
      );

      if (!response.ok) {
        // Handle specific HTTP status codes
        if (response.status === 429) {
          throw new Error("Too many requests. Please wait a moment and try again.");
        } else if (response.status === 403) {
          throw new Error("Invalid API key or quota exceeded.");
        } else if (response.status >= 500) {
          throw new Error("LocationIQ service temporarily unavailable.");
        } else {
          throw new Error(`LocationIQ API error: ${response.status}`);
        }
      }

      const data = await response.json();
      
      // Cache the results
      cacheRef.current.set(cacheKey, {
        results: data || [],
        timestamp: Date.now()
      });
      
      // Clean up old cache entries (keep last 50 entries)
      if (cacheRef.current.size > 50) {
        const entries = Array.from(cacheRef.current.entries()).sort((a, b) => b[1].timestamp - a[1].timestamp);
        cacheRef.current.clear();
        entries.slice(0, 50).forEach(([key, value]) => {
          cacheRef.current.set(key, value);
        });
      }
      
      // Reset retry count on successful request
      setRetryCount(0);
      setResults(data || []);
      setIsOpen(data?.length > 0);
      
    } catch (error) {
      console.error("LocationIQ search error:", error);
      
      // Retry logic for certain errors
      const maxRetries = 2;
      const isRetryableError = error instanceof Error && (
        error.message.includes("service temporarily unavailable") ||
        error.message.includes("timeout") ||
        error.name === "AbortError"
      );
      
      if (attemptCount < maxRetries && isRetryableError) {
        console.log(`Retrying search (attempt ${attemptCount + 1}/${maxRetries})`);
        setRetryCount(attemptCount + 1);
        
        // Exponential backoff: wait 1s, then 2s
        const delay = Math.pow(2, attemptCount) * 1000;
        setTimeout(() => {
          performSearch(searchQuery, attemptCount + 1);
        }, delay);
        return;
      }
      
      // Set error message for user feedback
      setError(error instanceof Error ? error.message : "Search failed. Please try again.");
      setResults([]);
      setIsOpen(false);
      setRetryCount(0);
    } finally {
      if (attemptCount === 0) { // Only set loading false for the initial attempt
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setSelectedIndex(-1);
    setError(null); // Clear errors when user starts typing
    
    // If user is editing a previously selected address, reset the selection tracking
    if (newValue !== lastSelectedAddress) {
      setLastSelectedAddress("");
    }
    
    // If user clears the input, close dropdown
    if (newValue.trim() === '') {
      setResults([]);
      setIsOpen(false);
      setLastSelectedAddress("");
    }
  };

  // Helper function to format a clean address from LocationIQ result
  const formatCleanAddress = (result: LocationIQResult): string => {
    const { address } = result;
    if (!address) return result.display_name;

    const parts = [];
    
    // Add street and house number
    if (address.road) {
      const streetPart = address.house_number 
        ? `${address.road} ${address.house_number}`
        : address.road;
      parts.push(streetPart);
    }
    
    // Add postal code and city
    const cityName = address.city || address.town || address.village || address.municipality || "";
    if (address.postcode && cityName) {
      parts.push(`${address.postcode} ${cityName}`);
    } else if (cityName) {
      parts.push(cityName);
    } else if (address.postcode) {
      parts.push(address.postcode);
    }
    
    return parts.length > 0 ? parts.join(", ") : result.display_name;
  };

  // Enhanced address component extraction with better fallbacks (similar to Mapbox logic)
  const extractAddressComponents = (result: LocationIQResult) => {
    const { address, display_name } = result;
    
    let streetName = address?.road || "";
    let streetNumber = address?.house_number || "";
    
    // Fallback: Extract from display_name if components are missing (matching Mapbox fallback logic)
    if ((!streetName || !streetNumber) && display_name) {
      const addressParts = display_name.split(",")[0]?.trim(); 
      if(addressParts) {
        const match = addressParts.match(/^(.*?)\s+(\d+[\w]*)$/);
        if (match) {
          if (!streetName) streetName = match[1]?.trim() ?? "";
          if (!streetNumber) streetNumber = match[2]?.trim() ?? "";
        }
        
        // Additional patterns for edge cases
        if (!streetName || !streetNumber) {
          const alternativePatterns = [
            /^(\d+[\w\-\/]*)\s+(.*?)$/, // "123 Musterstraße" (reversed)
            /^(.*?)\s+Nr\.?\s*(\d+[\w]*)$/i, // "Musterstraße Nr. 123"
            /^(.*?)\s+(\d+[\w]*\s*[a-z]?)$/i, // "Musterstraße 123 a"
          ];
          
          for (const pattern of alternativePatterns) {
            const altMatch = addressParts.match(pattern);
            if (altMatch) {
              if (!streetName) streetName = altMatch[1]?.trim() ?? "";
              if (!streetNumber) streetNumber = altMatch[2]?.trim() ?? "";
              break;
            }
          }
        }
      }
    }
    
    // Prioritize city over other location types (same priority as Mapbox)
    const cityName = address?.city || address?.town || address?.village || address?.municipality || "";
    
    return {
      streetName,
      streetNumber,
      cityName,
      postcode: address?.postcode || "",
      country: address?.country || "Deutschland",
    };
  };

  const handleResultSelect = (result: LocationIQResult) => {
    const cleanAddress = formatCleanAddress(result);
    const addressComponents = extractAddressComponents(result);

    setQuery(cleanAddress);
    setLastSelectedAddress(cleanAddress); // Track the selected address to prevent re-searching
    setIsOpen(false);
    setSelectedIndex(-1);
    setResults([]); // Clear results to prevent showing the same suggestion again
    setError(null); // Clear any existing errors

    // Transform LocationIQ result to match Mapbox format
    const transformedResult: LocationIQSearchResult = {
      features: [{
        place_name: cleanAddress, // What is displayed in the input
        geometry: {
          coordinates: [parseFloat(result.lon), parseFloat(result.lat)]
        },
        properties: {
          full_address: `${cleanAddress}, ${addressComponents.country}`, // Full address for context
          context: {
            address: {
              street_name: addressComponents.streetName,
              address_number: addressComponents.streetNumber,
            },
            place: {
              name: addressComponents.cityName,
            },
            postcode: {
              name: addressComponents.postcode,
            },
            country: {
              name: addressComponents.country,
            },
          },
        },
      }]
    };

    onRetrieve(transformedResult);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          const selectedResult = results[selectedIndex];
          if (selectedResult) {
            handleResultSelect(selectedResult);
          }
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <div className="flex items-center gap-1">
              {retryCount > 0 && (
                <span className="text-xs text-gray-500">{retryCount}</span>
              )}
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600"></div>
            </div>
          ) : error ? (
            <div className="h-4 w-4 text-red-500" title={error}>⚠️</div>
          ) : (
            <HiChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:border-gray-600 dark:bg-gray-700">
          {results.map((result, index) => {
            const cleanAddress = formatCleanAddress(result);
            
            return (
              <div
                key={result.place_id}
                onClick={() => handleResultSelect(result)}
                className={`flex cursor-pointer items-start gap-3 px-4 py-3 text-sm transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                  index === selectedIndex ? "bg-gray-50 dark:bg-gray-600" : ""
                } ${index === 0 ? "rounded-t-lg" : ""} ${index === results.length - 1 ? "rounded-b-lg" : ""}`}
              >
                <HiLocationMarker className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-gray-900 dark:text-white font-medium leading-5">
                    {cleanAddress}
                  </div>
                  {/* Show additional context if different from clean address */}
                  {cleanAddress !== result.display_name && (
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400 mt-1 leading-4">
                      {result.display_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {error && !isLoading && query.length >= 3 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-red-200 bg-red-50 p-3 shadow-lg dark:border-red-800 dark:bg-red-900">
          <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-200">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationIQSearch;